import type { CardGame } from './types'

const titleConnectorWords = new Set(['a', 'an', 'and', 'in', 'of', 'on', 'or', 'the', 'to'])
const basicLandNames = new Set([
  'forest',
  'island',
  'mountain',
  'plains',
  'swamp',
  'wastes',
])
const pokemonExactTitleCorrections: Record<string, string> = {
  sieesaarnewll: 'Ampharos',
}

export function extractLikelyCardTerms(
  rawText: string,
  options: { game?: CardGame; titleOnly?: boolean } = {},
): string[] {
  const rejected = [
    /^(basic|stage|stage1|stage2|trainer|item|instant|sorcery|creature|artifact|enchantment|planeswalker|land)\b/i,
    /^(destroy|target|whenever|as |you |this |it |if |choose|reveal)\b/i,
    /^enchant(?:ed)?\s+(?:target\s+)?(?:artifact|creature|land|permanent)\b/i,
    /^(discard|during|put |all special|your opponent|evolves from|when |although|this pokemon)\b/i,
    /^(growl|pika bolt|kindling panic|spectral breach|spooky shot|bite|cursed drop|poison sting|flap|claw slash)$/i,
    /^(illus|illustrated|copyright|weakness|resistance|retreat|ability|energy)\b/i,
    /^(tm|m\d+|ori|one|pal|sv|en |artist|power|toughness|no\.|hp|mp)\b/i,
    /\b\d{3,}\s*\/\s*\d{3,}\b/,
    /\b\d{2,3}\s*\/\s*\d{2,3}\b/,
    /\b(creature|artifact|enchantment|planeswalker|basic land)\s*[-—]/i,
    /[-—]\s*(human|assassin|aura|construct|sorin|swamp|plains)\b/i,
    /\b(wizards|coast|llc|copyright|artist|illustration|pokemon|nintendo|creatures|game freak)\b/i,
    /\b(weakness|resistance|retreat|poisoned|damage|counters|defending)\b/i,
    /\b(gets|has protection|cant be|can't be|library|battlefield|nonblack)\b/i,
    /^[^a-zA-Z]*$/,
  ]

  const candidates = rawText
    .split(/\r?\n/)
    .map((line) =>
      line
        .replace(/[|()[\]{}]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter((line) => !/^[\u2013\u2014]\s*[A-Z]/.test(line))
    .map((line) => cleanCardNameLine(line, options))
    .filter((line) => line.length >= 3 && line.length <= 64)
    .filter((line) => hasNameShape(line, options))
    .filter(
      (line) =>
        isAllowedPokemonTitle(line, options.game) ||
        !rejected.some((pattern) => pattern.test(line)),
    )

  const frequencies = new Map<string, number>()
  for (const candidate of candidates) {
    const key = candidate.toLowerCase()
    frequencies.set(key, (frequencies.get(key) ?? 0) + 1)
  }

  const seen = new Set<string>()
  return candidates
    .filter((candidate) => {
      const key = candidate.toLowerCase()
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
    .sort((left, right) => {
      const leftFrequency = Math.min(frequencies.get(left.toLowerCase()) ?? 1, 5)
      const rightFrequency = Math.min(frequencies.get(right.toLowerCase()) ?? 1, 5)
      const leftScore = cardNameShapeScore(left) + leftFrequency * 4
      const rightScore = cardNameShapeScore(right) + rightFrequency * 4
      return rightScore - leftScore
    })
    .slice(0, options.titleOnly ? 9 : 17)
}

export function mergeTerms(primary: string[], secondary: string[]): string[] {
  const seen = new Set<string>()
  const terms: string[] = []

  const addTerms = (candidates: string[], limit = 6) => {
    for (const term of candidates) {
      if (terms.length >= limit) {
        break
      }

      const key = term.toLowerCase()

      if (!seen.has(key)) {
        terms.push(term)
        seen.add(key)
      }
    }
  }

  addTerms(primary, 4)
  addTerms(secondary, 6)

  if (terms.length < 6) {
    addTerms(primary, 6)
  }

  return terms
}

export function rankConsensusTerms(groups: string[][]): string[] {
  const terms = groups.flat()
  const scored = terms
    .map((term, index) => {
      const normalized = normalizeConsensusTerm(term)
      const support = terms.reduce((total, other) => {
        const similarity = consensusSimilarity(
          normalized,
          normalizeConsensusTerm(other),
        )

        return total + (similarity >= 0.52 ? similarity : 0)
      }, 0)

      return {
        confidenceScore: cardNameShapeScore(term) + support * 4,
        index,
        nameScore: cardNameShapeScore(term),
        support,
        term,
      }
    })
    .sort(
      (left, right) =>
        right.confidenceScore - left.confidenceScore ||
        right.support - left.support ||
        right.nameScore - left.nameScore ||
        right.term.length - left.term.length ||
        left.index - right.index,
    )

  const selected: string[] = []

  for (const candidate of scored) {
    const normalized = normalizeConsensusTerm(candidate.term)
    const isNearDuplicate = selected.some(
      (term) =>
        consensusSimilarity(normalized, normalizeConsensusTerm(term)) >= 0.84,
    )

    if (!isNearDuplicate) {
      selected.push(candidate.term)
    }
  }

  return selected.slice(0, 9)
}

export function hasStrongTitleConsensus(
  groups: string[][],
  game: CardGame,
  minimumSupportingReads = 2,
): boolean {
  const ranked = rankConsensusTerms(groups)
  const leader = ranked[0]

  if (!leader || !hasTrustedTitleTerm([leader], game)) {
    return false
  }

  const normalizedLeader = normalizeConsensusTerm(leader)
  const supportingReads = groups.filter((group) =>
    group.some(
      (term) =>
        consensusSimilarity(
          normalizedLeader,
          normalizeConsensusTerm(term),
        ) >= 0.78,
    ),
  ).length

  return supportingReads >= minimumSupportingReads
}

function cardNameShapeScore(term: string): number {
  const words = term.split(/\s+/).filter(Boolean)
  const nameWords = words.filter((word, index) => {
    const letters = word.replace(/[^a-zA-Z]/g, '')
    return (
      /^[A-Z][a-z]{2,}$/.test(letters) ||
      isTitleConnectorWord(letters.toLowerCase(), index, words.length)
    )
  }).length
  const titleRatio = nameWords / Math.max(words.length, 1)

  return (
    nameWords * 3 +
    (titleRatio >= 0.67 ? 7 : 0) +
    (/[,'-]/.test(term) ? 3 : 0) -
    (titleRatio < 0.5 ? 7 : 0)
  )
}

function normalizeConsensusTerm(value: string): string {
  return value
    .toLowerCase()
    .replace(/[\u2018\u2019`\u00b4]/g, "'")
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function consensusSimilarity(left: string, right: string): number {
  if (!left || !right) {
    return 0
  }

  if (left === right) {
    return 1
  }

  const distance = consensusEditDistance(left, right)
  return 1 - distance / Math.max(left.length, right.length, 1)
}

function consensusEditDistance(left: string, right: string): number {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)
  const current = Array(right.length + 1).fill(0) as number[]

  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    current[0] = leftIndex

    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const cost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1
      current[rightIndex] = Math.min(
        current[rightIndex - 1] + 1,
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + cost,
      )
    }

    for (let index = 0; index < previous.length; index += 1) {
      previous[index] = current[index]
    }
  }

  return previous[right.length]
}

export function hasTrustedTitleTerm(terms: string[], game: CardGame): boolean {
  return terms.some((term) => {
    const words = term.split(/\s+/).filter(Boolean)
    const letters = term.replace(/[^a-zA-Z]/g, '')

    if (words.length > 1) {
      return cardNameShapeScore(term) >= 7
    }

    if (game === 'mtg' && basicLandNames.has(letters.toLowerCase())) {
      return true
    }

    return game === 'pokemon' && cardNameShapeScore(term) >= 3
  })
}

function cleanCardNameLine(
  line: string,
  options: { game?: CardGame; titleOnly?: boolean } = {},
): string {
  let cleaned = line
    .replace(/[\u2018\u2019`\u00b4]/g, "'")
    .replace(/[\u2010-\u2015]/g, '-')
    .replace(/\bBASIC\b/gi, ' ')
    .replace(/\bSTAGE\s*[12]?\b/gi, ' ')
    .replace(/\bTRAINER\b/gi, ' ')
    .replace(/\bITEM\b/gi, ' ')
    .replace(/\bEvolves from .+$/i, ' ')
    .replace(/\bHP\s*\d{1,3}\b/gi, ' ')
    .replace(/\b\d{1,3}\s*HP\b/gi, ' ')
    .replace(/\bmp\s*\d{1,3}\b/gi, ' ')
    .replace(/\bNo\.\s*[^ ]+.*$/i, ' ')
    .replace(/\b\d{1,3}\s*(lbs?|kg)\b/gi, ' ')
    .replace(/\bHT\.?.*$/i, ' ')
    .replace(/\s+[0-9xX]*[wubrgcWUBRGC]{1,5}[0-9xX]*\s*$/g, ' ')
    .replace(
      /\s+(?:[0-9xX]+|[wubrgcWUBRGC])(?:\s+(?:[0-9xX]+|[wubrgcWUBRGC])){0,8}$/g,
      ' ',
    )
    .replace(/\s+[0-9©®*•]+\s*$/g, '')
    .replace(/\s+[wubrgcWUBRGC]{1,5}\s*$/g, '')
    .replace(/^[^a-zA-Z]+/, '')
    .replace(/[^a-zA-Z0-9,' -]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()

  cleaned = cleanPokemonTitleLine(cleaned, options.game)
  cleaned = removeTrailingTitleNoise(cleaned)

  if (options.titleOnly) {
    cleaned = repairBasicLandTitle(cleaned, options.game)
    cleaned = repairTitleConnectors(cleaned)
    cleaned = repairPossessiveTitle(cleaned)
  }

  const words = cleaned.split(' ')
  const maxWords = options.titleOnly && options.game !== 'pokemon' ? 9 : 4

  if (words.length > maxWords) {
    cleaned = words.slice(0, maxWords).join(' ')
  }

  return cleaned
}

function isAllowedPokemonTitle(line: string, game: CardGame | undefined): boolean {
  return game === 'pokemon' && (line === 'Basic Energy' || line === 'Energy Swatter')
}

function repairBasicLandTitle(line: string, game: CardGame | undefined): string {
  if (game !== 'mtg') {
    return line
  }

  const normalized = line.replace(/[^a-zA-Z]/g, '').toLowerCase()
  const aliases: Record<string, string> = {
    forest: 'Forest',
    island: 'Island',
    isiand: 'Island',
    lsland: 'Island',
    mountain: 'Mountain',
    plains: 'Plains',
    sland: 'Island',
    swamp: 'Swamp',
    wastes: 'Wastes',
  }

  return aliases[normalized] ?? line
}

function repairTitleConnectors(line: string): string {
  return line
    .replace(/\bof\s*the\b/gi, 'of the')
    .replace(/\b(?:ot|0f|ol)\s+(?:the|tne|tha)\b/gi, 'of the')
    .replace(/\b(?:tne|tha)\b/gi, 'the')
}

function repairPossessiveTitle(line: string): string {
  return line
    .replace(/\b([A-Za-z]{3,})\s*'\s*s\b/g, "$1's")
    .replace(/\b([A-Za-z]{3,})\s+s\b(?=\s+[A-Za-z]{3,}\b)/g, "$1's")
}

function removeTrailingTitleNoise(line: string): string {
  const tokens = line.split(/\s+/).filter(Boolean)

  while (
    tokens.length >= 2 &&
    isLeadingTitleNoise(tokens[0]) &&
    tokens[1].replace(/[^a-zA-Z]/g, '').length >= 5
  ) {
    tokens.shift()
  }

  while (
    tokens.length >= 3 &&
    isLeadingTitleNoise(tokens[0]) &&
    tokens[1].replace(/[^a-zA-Z]/g, '').length >= 4
  ) {
    tokens.shift()
  }

  while (
    tokens.length > 1 &&
    isTrailingTitleNoise(tokens[tokens.length - 1], tokens.length - 1, tokens.length)
  ) {
    tokens.pop()
  }

  return tokens.join(' ')
}

function isLeadingTitleNoise(token: string): boolean {
  const cleaned = token.replace(/[^a-zA-Z]/g, '')
  return (
    (cleaned.length === 1 && cleaned.toLowerCase() !== 'a') ||
    /^[A-Z]{2}$/.test(cleaned)
  )
}

function isTrailingTitleNoise(token: string, index: number, wordCount: number): boolean {
  const cleaned = token.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
  const letters = token.replace(/[^a-zA-Z]/g, '')

  if (!cleaned) {
    return true
  }

  if (isTitleConnectorWord(cleaned, index, wordCount)) {
    return false
  }

  if (/^(?:III|II|IV|VI|VII|VIII|IX)$/i.test(letters)) {
    return false
  }

  if (
    wordCount >= 3 &&
    (/^[a-zA-Z]$/.test(token) ||
      /^[A-Z]{2,4}$/.test(letters) ||
      (letters.length <= 2 && !isProbablyTitleWord(letters)))
  ) {
    return true
  }

  return (
    /^[0-9x]+$/.test(cleaned) ||
    /^[0-9.]+$/.test(cleaned) ||
    /^[wubrgc]{1,3}$/.test(cleaned) ||
    /^[bo08oqd]{1,3}$/.test(cleaned) ||
    /^[wubrgco08oqd]{1,2}$/.test(cleaned) ||
    /^[0-9x]*[wubrgco08oqd]+[0-9x]*$/.test(cleaned) ||
    /^[a-z]{1,2}$/.test(cleaned) ||
    /^m[0-9]{2}$/.test(cleaned)
  )
}

function isProbablyTitleWord(value: string): boolean {
  return /^[A-Z][a-z]{2,}$/.test(value)
}

function cleanPokemonTitleLine(line: string, game: CardGame | undefined): string {
  if (game !== 'pokemon') {
    return line
  }

  return repairPokemonTitle(
    trimPokemonNoise(line)
    .replace(
      /^(?:BASIC|BASIG|BASICG|BASlC|BASIO|BASIC0|BASlO|BASI|BAS1C|B4SIC)\s*/i,
      ' ',
    )
    .replace(
      /^(?:BASIC|BASIG|BASICG|BASlC|BASIO|BASIC0|BASlO|BASI|BAS1C|B4SIC)(?=[A-Z][a-z])/i,
      '',
    )
    .replace(/^(?:STAGE|STACE|STAGF|S7AGE|STAG|STAGL)\s*[12ilzlt]?\s*/i, ' ')
    .replace(/^(?:STAGE|STACE|STAGF|S7AGE|STAG|STAGL)\s*[12ilzlt]?(?=[A-Z][a-z])/i, '')
    .replace(/^(?:TRAINER|TRAlNER|TR4INER|TRAlNFR)\s+(?:ITEM|lTEM|ITEMS|I[tf]em)?\s*/i, ' ')
    .replace(/^(?:TRAINER|TRAlNER|TR4INER|TRAlNFR)(?=[A-Z][a-z])/i, '')
    .replace(/^(?:ITEM|lTEM|I[tf]em|Ttem|Stem|ltem)\s*/i, ' ')
    .replace(/^(?:ITEM|lTEM|I[tf]em|Ttem|Stem|ltem)(?=[A-Z][a-z])/i, '')
    .replace(/\b(?:BASIC|STAGE\s*[12]?|TRAINER|ITEM)\b/gi, ' ')
    .replace(
      /\b(?:BASIG|BASICG|BASlC|BASIO|BASIC0|BASlO|BASI|BAS1C|B4SIC|STACE|STAGF|S7AGE|STAGL|TRAlNER|TR4INER|TRAlNFR)\b/gi,
      ' ',
    )
    .replace(/^[A-Z]\s+(?=[A-Z][a-z]{2,}\b)/, ' ')
    .replace(/^(?:[A-Z]?\s*)?(?:Evolves|Evolve)\s+from\b.*$/i, ' ')
    .replace(/\b(?:HP|MP)\s*[A-Za-z]*/gi, ' ')
    .replace(/\b(?:Evolves|Evolve|from)\b.*$/i, ' ')
    .replace(/\s+/g, ' ')
    .trim(),
  )
}

export function repairPokemonTitle(line: string): string {
  const normalized = line.replace(/[^a-zA-Z]/g, '').toLowerCase()
  const exactCorrection = pokemonExactTitleCorrections[normalized]

  if (exactCorrection) {
    return exactCorrection
  }

  if (/energyswatter/.test(normalized)) {
    return 'Energy Swatter'
  }

  if (/(energy|encrgy|cnergy|cnerdy|enerqy|enerdy|emelay|encrdy|enorgy)/.test(normalized)) {
    return 'Basic Energy'
  }

  return line
}

export function trimPokemonNoise(line: string): string {
  return trimPokemonTrailingNoise(stripPokemonLeadingNoise(line))
}

function stripPokemonLeadingNoise(line: string): string {
  const repaired = line
    .replace(/\b([a-z])([A-Z][a-z]{3,})\b/g, '$2')
    .replace(/\s+/g, ' ')
    .trim()
  const words = repaired.split(/\s+/).filter(Boolean)

  while (
    words.length > 1 &&
    pokemonLeadingNoise.has(cleanPokemonNoiseToken(words[0])) &&
    hasPokemonTitleToken(words.slice(1))
  ) {
    words.shift()
  }

  return words.join(' ')
}

export function trimPokemonTrailingNoise(line: string): string {
  const words = line.split(/\s+/).filter(Boolean)

  while (
    words.length > 1 &&
    pokemonTrailingNoise.has(cleanPokemonNoiseToken(words[words.length - 1])) &&
    hasPokemonTitleToken(words.slice(0, -1))
  ) {
    words.pop()
  }

  return words.join(' ')
}

const pokemonLeadingNoise = new Set([
  'f',
  'go',
  'j',
  's',
  'sh',
  'x',
  'y',
])

const pokemonTrailingNoise = new Set([
  'a',
  'ac',
  'hp',
  'ht',
  'i',
  'io',
  'j',
  'ny',
  'pd',
  'pod',
  'pra',
  'pre',
  'pro',
  'wio',
  'wt',
  'yy',
])

function cleanPokemonNoiseToken(token: string): string {
  return token.replace(/[^a-zA-Z]/g, '').toLowerCase()
}

function hasPokemonTitleToken(words: string[]): boolean {
  return words.some((word) => /^[A-Z][a-z]{2,}$/.test(word.replace(/[^a-zA-Z]/g, '')))
}

function hasNameShape(line: string, options: { titleOnly?: boolean } = {}): boolean {
  const letters = line.replace(/[^a-zA-Z]/g, '')
  const words = line.split(/\s+/).filter(Boolean)

  const maxWords = options.titleOnly ? 9 : 5

  if (letters.length < 3 || words.length > maxWords) {
    return false
  }

  if (
    options.titleOnly &&
    words.length > 1 &&
    words.some((word, index) => {
      const lettersOnly = word.replace(/[^a-zA-Z]/g, '').toLowerCase()

      return (
        lettersOnly.length < 3 &&
        !isTitleConnectorWord(lettersOnly, index, words.length)
      )
    })
  ) {
    return false
  }

  return words.every((word) => /[a-zA-Z]/.test(word))
}

function isTitleConnectorWord(word: string, index: number, wordCount: number): boolean {
  return index > 0 && index < wordCount - 1 && titleConnectorWords.has(word)
}
